import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ImageUploader } from "@/components/menu/ImageUploader";
import { Palette, Copy, ExternalLink } from "lucide-react";
import { useBrandStore } from "@/store/brandStore";
import { Input } from "@/components/ui/input";
import { useAuthStore } from "@/store/authStore";
import {
  fetchBrandSettings,
  updateBrandSettings,
  type BrandSettingsForm,
} from "@/services/settings";
import { useTranslation } from "react-i18next";
import { toast } from "@/hooks/use-toast";
import { useStoreManagementStore } from "@/store/storeManagementStore";

export function BrandSettings() {
  const { name, logo, colors, setName, setLogo, setColors, applyColorsToCss } =
    useBrandStore();
  const { user } = useAuthStore();
  const { selectedStore: store } = useStoreManagementStore();
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();

  // Local state for form
  const [brandName, setBrandName] = useState(name);
  const [selectedColors, setSelectedColors] = useState(colors);
  const [isManager, setIsManager] = useState(false);

  // Check if user is a manager or higher
  useEffect(() => {
    if (user?.role) {
      setIsManager(["manager", "admin", "super_admin"].includes(user.role));
    }
  }, [user?.role]);

  // Load brand settings from database
  useEffect(() => {
    const loadBrandSettings = async () => {
      if (!user?.id) return;

      try {
        const settings = await fetchBrandSettings(user.id);

        if (settings) {
          // Update local state
          setBrandName(settings.name || "");
          setName(settings.name || "");

          if (settings.logo) {
            setLogo(settings.logo);
          }

          const brandColors = {
            primary: settings.primary_color,
            secondary: settings.secondary_color,
            accent: settings.accent_color,
          };

          setSelectedColors(brandColors);
          setColors(brandColors);
        }
      } catch (error) {
        console.error("Error loading brand settings:", error);
      }
    };

    loadBrandSettings();
  }, [user?.id, setColors, setLogo, setName]);

  // Apply the colors from store when component mounts to ensure consistency
  useEffect(() => {
    applyColorsToCss();
  }, [applyColorsToCss]);

  const handleLogoUpload = (imageDataUrl: string | null) => {
    setLogo(imageDataUrl || "");
  };

  const handleColorChange = (
    type: "primary" | "secondary" | "accent",
    value: string
  ) => {
    // Update local state first for a responsive UI
    setSelectedColors({ ...selectedColors, [type]: value });

    // Apply color change immediately for real-time preview
    setColors({ [type]: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id || !isManager) return;

    setIsLoading(true);

    try {
      const brandData: BrandSettingsForm = {
        name: brandName,
        logo: logo,
        primary_color: selectedColors.primary,
        secondary_color: selectedColors.secondary,
        accent_color: selectedColors.accent,
      };

      await updateBrandSettings(user.id, brandData);

      // Update global state
      setName(brandName);
    } catch (error) {
      console.error("Error saving brand settings:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const storefrontUrl = store?.slug
    ? `${window.location.origin}/store/${store.slug}`
    : "";
  const reservationUrl = store?.slug
    ? `${window.location.origin}/reservation/${store.slug}`
    : "";

  const copyToClipboard = (
    text: string,
    type: "storefront" | "reservation"
  ) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast({
      title: t(`brandSettings.urls.${type}.copied`),
    });
  };

  // If user isn't a manager, show a message
  if (!isManager) {
    return (
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>{t("brandSettings")}</CardTitle>
          <CardDescription>{t("managerPermissionsRequired")}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <CardTitle>{t("brandIdentity")}</CardTitle>
            <CardDescription>{t("customizeBrandAppearance")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="brandName">{t("brandName")}</Label>
              <Input
                id="brandName"
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <Label>{t("brandLogo")}</Label>
              <ImageUploader
                onImageUploaded={handleLogoUpload}
                currentImage={logo}
              />
              {logo && (
                <div className="mt-2 p-2 border rounded-md bg-muted/30">
                  <p className="text-xs text-muted-foreground mb-1">
                    {t("currentLogo")}
                  </p>
                  <img src={logo} alt={t("brandLogo")} className="h-10" />
                </div>
              )}
            </div>

            <div className="space-y-4">
              <Label>{t("brandColors")}</Label>

              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: selectedColors.primary }}
                    />
                    <span className="text-sm font-medium">
                      {t("primaryColor")}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={selectedColors.primary}
                      onChange={(e) =>
                        handleColorChange("primary", e.target.value)
                      }
                      className="w-full h-10 rounded-md cursor-pointer"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: selectedColors.secondary }}
                    />
                    <span className="text-sm font-medium">
                      {t("secondaryColor")}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={selectedColors.secondary}
                      onChange={(e) =>
                        handleColorChange("secondary", e.target.value)
                      }
                      className="w-full h-10 rounded-md cursor-pointer"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: selectedColors.accent }}
                    />
                    <span className="text-sm font-medium">
                      {t("accentColor")}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={selectedColors.accent}
                      onChange={(e) =>
                        handleColorChange("accent", e.target.value)
                      }
                      className="w-full h-10 rounded-md cursor-pointer"
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </div>

              <div className="mt-4 p-4 border rounded-md bg-muted/30">
                <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <Palette className="w-4 h-4" /> {t("colorPreview")}
                </h4>
                <div className="flex flex-wrap gap-2">
                  <Button>{t("primaryButton")}</Button>
                  <Button variant="secondary">{t("secondaryButton")}</Button>
                  <Button
                    variant="outline"
                    className="border-accent text-accent hover:bg-accent/10"
                  >
                    {t("accentButton")}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* URLs Section */}
        <Card>
          <CardHeader>
            <CardTitle>{t("brandSettings.urls.title")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Storefront URL */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t("brandSettings.urls.storefront.label")}
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={storefrontUrl}
                  readOnly
                  className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(storefrontUrl, "storefront")}
                >
                  <Copy className="h-4 w-4 mr-2" />
                  {t("brandSettings.urls.storefront.copy")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(storefrontUrl, "_blank")}
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Reservation URL */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t("brandSettings.urls.reservation.label")}
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={reservationUrl}
                  readOnly
                  className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(reservationUrl, "reservation")}
                >
                  <Copy className="h-4 w-4 mr-2" />
                  {t("brandSettings.urls.reservation.copy")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(reservationUrl, "_blank")}
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button
            type="submit"
            className="w-full sm:w-auto"
            disabled={isLoading}
          >
            {isLoading ? t("savingBrandSettings") : t("saveBrandSettings")}
          </Button>
        </div>
      </div>
    </form>
  );
}
